import React, { useMemo, useState } from 'react';
import { Modal, Box, Alert } from '@cloudscape-design/components';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  ModuleRegistry,
} from 'ag-grid-community';
import { RowGroupingModule, ServerSideRowModelModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getControlPivotData } from 'src/api/hooks/getControlPivotData';

ModuleRegistry.registerModules([RowGroupingModule, ServerSideRowModelModule]);

interface PivotTableModalProps {
  controlId: string;
  filters: Record<string, string[]>;
  visible: boolean;
  onDismiss: () => void;
}

export const PivotTableModal = ({
  controlId,
  filters,
  visible,
  onDismiss,
}: PivotTableModalProps) => {
  const [hasError, setHasError] = useState(false);

  // TODO: add pivot column defs to ControlAttributesMetadata
  const columnDefs: ColDef[] = [
    { field: 'costCenter', rowGroup: true },
    {
      field: 'account',
      rowGroup: true,
      valueGetter: function (params) {
        const accountNumber = params.data.accountNumber;
        const accountDescription = params.data.accountDescription;
        return `${accountNumber} - ${accountDescription}`;
      },
    },
  ];

  const datasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        if (Object.keys(filters).length <= 1) {
          params.success({ rowData: [], rowCount: 0 });
          return;
        }

        setHasError(false);

        getControlPivotData(controlId, filters, [], '', [], params.request.groupKeys)
          .then((response) => {
            if (params.request.groupKeys.length === 0) {
              params.success({
                rowData: response.rowData,
                rowCount: response.rowData.length,
                pivotResultFields: response.pivotResultFields,
              });
            } else {
              params.success({
                rowData: response.rowData,
                rowCount: response.rowData.length,
              });
            }
          })
          .catch(() => {
            setHasError(true);
            params.fail();
          });
      },
    }),
    [controlId, filters]
  );

  return (
    <Modal visible={visible} onDismiss={onDismiss} header="Pivot Table View" size="max">
      <Box padding="l">
        {hasError && (
          <Alert type="error" data-testid="pivot-data-error-alert">
            The pivot data is currently unable to be displayed in the UI. The data is available in
            the back-end for export, please proceed with exporting the report as normal. If you
            would like to review the data in the UI, please refresh your browser and re-run your
            report.
          </Alert>
        )}
        <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
          {visible && (
            <AgGridReact
              key={JSON.stringify(filters)}
              columnDefs={columnDefs}
              rowModelType="serverSide"
              serverSideDatasource={datasource}
              pivotMode={true}
              serverSidePivotResultFieldSeparator={'##'}
            />
          )}
        </div>
      </Box>
    </Modal>
  );
};
